import {
  Configuration,
  Custom,
  DvmData,
  DvmStyles,
  LegalTextData,
  Theme,
  Tutorial,
} from '@core/models';
import { checkoutTutorialSteps } from '../tutorial/checkout/steps';
import {
  seatSelectionDesktopTutorialSteps,
  seatSelectionMobileTutorialSteps,
} from '../tutorial/seat-selection/steps';

const custom: Custom = {
  paymentPage: {
    captureAddress: false,
    capturePhone: false,
  },
};

const theme: Theme = {
  'default-color': '0 0 0',
  'main-color': '0 0 0',
  'accent-color': '0 163 224',
  'secondary-color': '25 149 255',
  'tertiary-color': '242 246 250',
  red: '189 48 57',
  success: '86 163 67',
  'success-off-white': '236 246 232',
};

const dvmData: DvmData = {
  venueId: 'nam-us-10182-marlins',
  mapId: 'main_venuemap',
  miniMapId: 'main_venuemap_minimap',
  miniMapEnabled: true,
  gaSectorsIdArray: [],
  gaAdaIdArray: [],
  sectionOffsets: {},
  limitedZoom: false,
};

const dvmStyles: DvmStyles = {
  'seat-available-none-fill': '25 149 255',
  'seat-available-none-stroke': '0 90 156',
  'seat-available-disabled-fill': '25 149 255',
  'seat-available-disabled-stroke': '0 90 156',
  'seat-unavailable-none': '214 214 214',
  'seat-available-pending': '230 145 56',
  'section-available-normal-fill': '25 149 255',
  'section-available-normal-stroke': '0 90 156',
  'section-available-normal-group1-fill': '255 77 77',
  'section-available-normal-group1-stroke': '255 77 77',
  'section-available-hover-fill': '0 90 156',
  'section-available-hover-stroke': '255 255 255',
  'section-selected-normal-fill': '225 56 63',
  'section-selected-normal-stroke': '225 56 63',
  'section-selected-hover-stroke': '255 255 255',
};

const seatSelectionDesktopTutorial: Tutorial = {
  sectionAvailabilityIds: ['1857', '1818', '1883', '1869', '1903'],
  availableSeatsQuantity: 240,
  selectedSeatsQuantity: 30,
  allowClose: false,
  doneBtnText: 'End Tutorial',
  nextBtnText: 'Next',
  popoverClass: 'driverjs-theme',
  prevBtnText: 'Back',
  steps: seatSelectionDesktopTutorialSteps,
};

const seatSelectionMobileTutorial: Tutorial = {
  ...seatSelectionDesktopTutorial,
  steps: seatSelectionMobileTutorialSteps,
};

const checkoutTutorial: Tutorial = {
  ...seatSelectionDesktopTutorial,
  steps: checkoutTutorialSteps,
};

export const MiamiMarlinsConfig: Configuration = {
  id: 'miamimarlins',
  clientName: 'Miami Marlins',
  appName: 'Miami Marlins',
  city: 'Miami',
  favicon: '/assets/img/miamimarlins/favicon.png',
  logo: '/assets/img/miamimarlins/logo.png',
  phone: '',
  email: 'groupsales@miamimarlins.com',
  paymentMethod: 'braintree',
  rainout: false,
  upsells: true,
  clientWebUrl: 'https://www.marlins.com/',
  clientWebText: 'www.marlins.com.',
  termsOfUseUrl: 'https://www.mlb.com/official-information/terms-of-use',
  clientBallParkUrl:
    'https://www.mlb.com/marlins/apps/ballpark?partnerId=redirect-chc-ballparkapp',
  clientBallParkText: 'marlins.com/BallparkApp',
  mlbShopUrl:
    'https://www.mlbshopeurope.com/en/miami-marlins/t-25000926+z-73041-754230621',
  merchandiseWebUrl: '',
  rainoutInsuranceUrl: '',
  termsAndConditionsUrl:
    'https://www.mlb.com/marlins/tickets/terms-and-conditions',
  analytics: '',
  adas: true,
  custom,
  theme,
  dvmData,
  dvmStyles,
  tutorial: {
    seatSelection: {
      desktop: seatSelectionDesktopTutorial,
      mobile: seatSelectionMobileTutorial,
    },
    checkout: { desktop: checkoutTutorial, mobile: checkoutTutorial },
  },
  extraCheckbox: {
    active: false,
    required: true,
    text: '',
  },
  cartSummaryText: 'Here you can see your new requested tickets',
  summaryPage: {
    image1: '/assets/img/miamimarlins/logo.png',
    image2: '/assets/img/miamimarlins/logo.png',
  },
  get legalText(): LegalTextData {
    return {
      links: {
        termsLink: {
          href: this.termsAndConditionsUrl,
          text: 'Terms and Conditions',
          target: '_blank',
        },
        clubWebLink: {
          href: this.clientWebUrl,
          text: this.clientWebText,
          target: '_blank',
        },
      },
      text: [
        `As part of your ticket purchase, you are required to acknowledge the {termsLink}.
        The Terms and conditions of your ticket and policies applicable to its use are updated to
        address circumstances related to COVID-19, health and safety, and seating pods.
        By clicking the Place Order button, you agree to accept all {termsLink} applicable to ticket purchase, and all related policies.
        Further, due to the COVID-19 pandemic, scheduling may be impacted and games may be canceled,
        rescheduled, postponed or games may be held with limited capacity. Games may also have restricted capacity that prevents the use of specific
        seats. In the event that the game(s) for which the ticket(s) is issued does not occur or you are unable to use the
        specific seat(s) purchased, a refund or credit will be issued according to the refund policy available at {clubWebLink}.`,

        `By selecting the Place Order button you agree to purchase the tickets selected, to accept any restrictions noted, and
        understand you are responsible for any payment needed to complete the purchase. Upon completing your purchase by
        selecting the Place Order button, you acknowledge and agree your credit card will be charged. All sales are final. All
        tickets are subject to the {termsLink}.`,

        `In connection with your purchase, you may receive customer service-related emails from the ${this.clientName}, including
        optional surveys regarding your gameday experience. You also may receive ticket-related commercial email from the
        ${this.clientName}. You may unsubscribe from ticket-related commercial emails at any time.`,
      ],
    };
  },
  defaultTooltipText: 'Review seats before continuing',
  teamId: '146',
  metaPixelAnalytics: false,
};
